import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Input,
  Button,
  Switch,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  toast
} from '@tonic/shared/components/ui';
import { useResaleSettings, useUpdateResaleSettings } from '@/features/re-commerce/api/hooks';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

function SyncStatus() {
  const [status, setStatus] = React.useState('checking');

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        // In development, use mock data
        if (process.env.NODE_ENV === 'development') {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          setStatus('synced');
          return;
        }

        const response = await fetch('/api/resale/settings/sync-status', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }

        const data = await response.json();
        setStatus(data.status || 'unknown');
      } catch (error) {
        console.error('Failed to check sync status:', error);
        // In development, don't show error state
        if (process.env.NODE_ENV === 'development') {
          setStatus('synced');
        } else {
          setStatus('error');
        }
      }
    };

    // Initial check
    checkStatus();

    // Set up polling every 30 seconds
    const interval = setInterval(checkStatus, 30000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  if (status === 'checking') {
    return (
      <div className="flex items-center gap-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
        <span className="text-sm text-muted-foreground">Checking sync status...</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <ExclamationCircleIcon className="h-5 w-5" />
        <span className="text-sm">Sync error - Check API connection</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-secondary">
      <CheckCircleIcon className="h-5 w-5" />
      <span className="text-sm">Settings synced</span>
    </div>
  );
}

const tabStyles = "px-4 py-2 rounded-md hover:bg-neutral-100 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap";

export function ResaleSettingsPage() {
  const { data: settings, isLoading } = useResaleSettings();
  const { mutate: updateSettings, isLoading: isUpdating } = useUpdateResaleSettings();
  const [formData, setFormData] = React.useState({
    // General Program Settings
    programName: '',
    description: '',
    defaultPricingStrategy: 'fixed',
    minimumCondition: 'good',
    autoListItems: false,

    // Pricing & Financial Settings
    commissionRate: '10',
    minimumPrice: '10',
    maximumPrice: '1000',
    pricingStrategy: 'dynamic',
    acceptedPaymentMethods: ['credit_card', 'paypal'],

    // Quality & Inspection Settings
    requireInspectionPhotos: true,
    qualityGrades: ['A', 'B', 'C'],
    inspectionChecklist: '',
    photoRequirements: '',

    // Shipping & Logistics
    defaultShippingMethod: 'standard',
    shippingProviders: ['usps', 'fedex', 'ups'],
    packagingRequirements: '',
    returnShippingMethod: 'standard',

    // Inventory Management
    lowStockThreshold: '5',
    autoRemoveAfterDays: '90',
    locationTracking: true,
    barcodeScanning: true,

    // Customer Experience
    returnPeriod: '30',
    customerServiceEmail: '',
    automaticEmails: true,
    productDescriptionTemplate: '',

    // Environmental Impact
    carbonOffsetEnabled: true,
    recyclingPartners: '',
    sustainabilityMetrics: true,
    impactReporting: true,

    // Notifications & Alerts
    notificationEmails: '',
    alertThresholds: {
      lowStock: '5',
      highDemand: '20',
      priceChange: '10'
    },

    // Integration Settings
    marketplaceIntegrations: ['ebay', 'poshmark'],
    apiEndpoints: '',
    webhookUrls: '',

    // Legal & Compliance
    termsAndConditions: '',
    privacyPolicy: '',
    returnPolicy: '',
    prohibitedItems: '',

    // Analytics & Reporting
    analyticsEnabled: true,
    reportSchedule: 'weekly',
    customMetrics: '',

    // Access Control
    rolePermissions: {
      admin: ['all'],
      manager: ['view', 'edit'],
      staff: ['view']
    },
    approvalWorkflow: true
  });

  React.useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Update settings in admin database
      const result = await updateSettings(formData);

      // Trigger settings sync
      const syncResponse = await fetch('/api/settings/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!syncResponse.ok) {
        throw new Error('Failed to sync settings with personal commerce app');
      }

      toast({
        title: 'Settings updated',
        description: 'Changes have been saved and synced with the personal commerce app.',
        status: 'success'
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings',
        status: 'error'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded mb-6" />
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Resale Settings</h1>
        <SyncStatus />
      </div>

      <Tabs defaultValue="general">
        <div className="border-b border-neutral-200 mb-6 overflow-x-auto">
          <TabsList className="flex gap-1 bg-transparent min-w-max">
            <TabsTrigger className={tabStyles} value="general">General</TabsTrigger>
            <TabsTrigger className={tabStyles} value="pricing">Pricing & Financial</TabsTrigger>
            <TabsTrigger className={tabStyles} value="quality">Quality & Inspection</TabsTrigger>
            <TabsTrigger className={tabStyles} value="shipping">Shipping & Logistics</TabsTrigger>
            <TabsTrigger className={tabStyles} value="inventory">Inventory</TabsTrigger>
            <TabsTrigger className={tabStyles} value="customer">Customer Experience</TabsTrigger>
            <TabsTrigger className={tabStyles} value="environmental">Environmental</TabsTrigger>
            <TabsTrigger className={tabStyles} value="notifications">Notifications</TabsTrigger>
            <TabsTrigger className={tabStyles} value="integrations">Integrations</TabsTrigger>
            <TabsTrigger className={tabStyles} value="legal">Legal</TabsTrigger>
            <TabsTrigger className={tabStyles} value="analytics">Analytics</TabsTrigger>
            <TabsTrigger className={tabStyles} value="access">Access Control</TabsTrigger>
          </TabsList>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Program Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="programName">Program Name</Label>
                    <Input
                      id="programName"
                      value={formData.programName}
                      onChange={(e) => handleChange('programName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="defaultPricingStrategy">Default Pricing Strategy</Label>
                    <Select
                      value={formData.defaultPricingStrategy}
                      onValueChange={(value) => handleChange('defaultPricingStrategy', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Price</SelectItem>
                        <SelectItem value="dynamic">Dynamic Pricing</SelectItem>
                        <SelectItem value="auction">Auction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Program Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Auto-List Items</Label>
                    <div className="text-sm text-neutral-500">
                      Automatically list items after quality check
                    </div>
                  </div>
                  <Switch
                    checked={formData.autoListItems}
                    onCheckedChange={(value) => handleChange('autoListItems', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle>Pricing & Financial Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="commissionRate">Commission Rate (%)</Label>
                    <Input
                      id="commissionRate"
                      type="number"
                      value={formData.commissionRate}
                      onChange={(e) => handleChange('commissionRate', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pricingStrategy">Pricing Strategy</Label>
                    <Select
                      value={formData.pricingStrategy}
                      onValueChange={(value) => handleChange('pricingStrategy', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="dynamic">Dynamic</SelectItem>
                        <SelectItem value="algorithmic">Algorithmic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="minimumPrice">Minimum Price ($)</Label>
                    <Input
                      id="minimumPrice"
                      type="number"
                      value={formData.minimumPrice}
                      onChange={(e) => handleChange('minimumPrice', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maximumPrice">Maximum Price ($)</Label>
                    <Input
                      id="maximumPrice"
                      type="number"
                      value={formData.maximumPrice}
                      onChange={(e) => handleChange('maximumPrice', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quality">
            <Card>
              <CardHeader>
                <CardTitle>Quality & Inspection Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="qualityGrades">Quality Grades</Label>
                    <Select
                      value={formData.qualityGrades[0]}
                      onValueChange={(value) => handleChange('qualityGrades', [...formData.qualityGrades, value])}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">Grade A (Like New)</SelectItem>
                        <SelectItem value="B">Grade B (Good)</SelectItem>
                        <SelectItem value="C">Grade C (Fair)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="photoRequirements">Photo Requirements</Label>
                    <Textarea
                      id="photoRequirements"
                      value={formData.photoRequirements}
                      onChange={(e) => handleChange('photoRequirements', e.target.value)}
                      placeholder="Enter photo requirements..."
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inspectionChecklist">Inspection Checklist</Label>
                  <Textarea
                    id="inspectionChecklist"
                    value={formData.inspectionChecklist}
                    onChange={(e) => handleChange('inspectionChecklist', e.target.value)}
                    rows={4}
                    placeholder="Enter inspection checklist items..."
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Require Inspection Photos</Label>
                    <div className="text-sm text-neutral-500">
                      Require photos during item inspection
                    </div>
                  </div>
                  <Switch
                    checked={formData.requireInspectionPhotos}
                    onCheckedChange={(value) => handleChange('requireInspectionPhotos', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shipping">
            <Card>
              <CardHeader>
                <CardTitle>Shipping & Logistics Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="defaultShippingMethod">Default Shipping Method</Label>
                    <Select
                      value={formData.defaultShippingMethod}
                      onValueChange={(value) => handleChange('defaultShippingMethod', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="express">Express</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="returnShippingMethod">Return Shipping Method</Label>
                    <Select
                      value={formData.returnShippingMethod}
                      onValueChange={(value) => handleChange('returnShippingMethod', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="express">Express</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="packagingRequirements">Packaging Requirements</Label>
                  <Textarea
                    id="packagingRequirements"
                    value={formData.packagingRequirements}
                    onChange={(e) => handleChange('packagingRequirements', e.target.value)}
                    rows={4}
                    placeholder="Enter packaging requirements..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shipping Providers</Label>
                  <div className="flex gap-2">
                    {formData.shippingProviders.map((provider) => (
                      <Badge key={provider}>{provider.toUpperCase()}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory">
            <Card>
              <CardHeader>
                <CardTitle>Inventory Management Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lowStockThreshold">Low Stock Threshold</Label>
                    <Input
                      id="lowStockThreshold"
                      type="number"
                      value={formData.lowStockThreshold}
                      onChange={(e) => handleChange('lowStockThreshold', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="autoRemoveAfterDays">Auto-Remove After (Days)</Label>
                    <Input
                      id="autoRemoveAfterDays"
                      type="number"
                      value={formData.autoRemoveAfterDays}
                      onChange={(e) => handleChange('autoRemoveAfterDays', e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Location Tracking</Label>
                    <div className="text-sm text-neutral-500">
                      Enable warehouse location tracking
                    </div>
                  </div>
                  <Switch
                    checked={formData.locationTracking}
                    onCheckedChange={(value) => handleChange('locationTracking', value)}
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Barcode Scanning</Label>
                    <div className="text-sm text-neutral-500">
                      Enable barcode scanning for inventory
                    </div>
                  </div>
                  <Switch
                    checked={formData.barcodeScanning}
                    onCheckedChange={(value) => handleChange('barcodeScanning', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customer">
            <Card>
              <CardHeader>
                <CardTitle>Customer Experience Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="returnPeriod">Return Period (Days)</Label>
                    <Input
                      id="returnPeriod"
                      type="number"
                      value={formData.returnPeriod}
                      onChange={(e) => handleChange('returnPeriod', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerServiceEmail">Customer Service Email</Label>
                    <Input
                      id="customerServiceEmail"
                      type="email"
                      value={formData.customerServiceEmail}
                      onChange={(e) => handleChange('customerServiceEmail', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="productDescriptionTemplate">Product Description Template</Label>
                  <Textarea
                    id="productDescriptionTemplate"
                    value={formData.productDescriptionTemplate}
                    onChange={(e) => handleChange('productDescriptionTemplate', e.target.value)}
                    rows={4}
                    placeholder="Enter product description template..."
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Automatic Emails</Label>
                    <div className="text-sm text-neutral-500">
                      Send automatic emails for order updates
                    </div>
                  </div>
                  <Switch
                    checked={formData.automaticEmails}
                    onCheckedChange={(value) => handleChange('automaticEmails', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="environmental">
            <Card>
              <CardHeader>
                <CardTitle>Environmental Impact Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recyclingPartners">Recycling Partners</Label>
                  <Textarea
                    id="recyclingPartners"
                    value={formData.recyclingPartners}
                    onChange={(e) => handleChange('recyclingPartners', e.target.value)}
                    rows={4}
                    placeholder="Enter recycling partner information..."
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Carbon Offset</Label>
                    <div className="text-sm text-neutral-500">
                      Enable carbon offset for shipments
                    </div>
                  </div>
                  <Switch
                    checked={formData.carbonOffsetEnabled}
                    onCheckedChange={(value) => handleChange('carbonOffsetEnabled', value)}
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Sustainability Metrics</Label>
                    <div className="text-sm text-neutral-500">
                      Track sustainability metrics
                    </div>
                  </div>
                  <Switch
                    checked={formData.sustainabilityMetrics}
                    onCheckedChange={(value) => handleChange('sustainabilityMetrics', value)}
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Impact Reporting</Label>
                    <div className="text-sm text-neutral-500">
                      Generate environmental impact reports
                    </div>
                  </div>
                  <Switch
                    checked={formData.impactReporting}
                    onCheckedChange={(value) => handleChange('impactReporting', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notifications & Alerts Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="notificationEmails">Notification Emails</Label>
                  <Input
                    id="notificationEmails"
                    value={formData.notificationEmails}
                    onChange={(e) => handleChange('notificationEmails', e.target.value)}
                    placeholder="Enter comma-separated email addresses"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lowStockAlert">Low Stock Alert Threshold</Label>
                    <Input
                      id="lowStockAlert"
                      type="number"
                      value={formData.alertThresholds.lowStock}
                      onChange={(e) => handleChange('alertThresholds', { ...formData.alertThresholds, lowStock: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="highDemandAlert">High Demand Alert Threshold</Label>
                    <Input
                      id="highDemandAlert"
                      type="number"
                      value={formData.alertThresholds.highDemand}
                      onChange={(e) => handleChange('alertThresholds', { ...formData.alertThresholds, highDemand: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations">
            <Card>
              <CardHeader>
                <CardTitle>Integration Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Marketplace Integrations</Label>
                  <div className="flex gap-2">
                    {formData.marketplaceIntegrations.map((marketplace) => (
                      <Badge key={marketplace}>{marketplace}</Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiEndpoints">API Endpoints</Label>
                  <Textarea
                    id="apiEndpoints"
                    value={formData.apiEndpoints}
                    onChange={(e) => handleChange('apiEndpoints', e.target.value)}
                    rows={4}
                    placeholder="Enter API endpoint configurations..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhookUrls">Webhook URLs</Label>
                  <Textarea
                    id="webhookUrls"
                    value={formData.webhookUrls}
                    onChange={(e) => handleChange('webhookUrls', e.target.value)}
                    rows={4}
                    placeholder="Enter webhook URLs..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="legal">
            <Card>
              <CardHeader>
                <CardTitle>Legal & Compliance Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="termsAndConditions">Terms and Conditions</Label>
                  <Textarea
                    id="termsAndConditions"
                    value={formData.termsAndConditions}
                    onChange={(e) => handleChange('termsAndConditions', e.target.value)}
                    rows={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="privacyPolicy">Privacy Policy</Label>
                  <Textarea
                    id="privacyPolicy"
                    value={formData.privacyPolicy}
                    onChange={(e) => handleChange('privacyPolicy', e.target.value)}
                    rows={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="returnPolicy">Return Policy</Label>
                  <Textarea
                    id="returnPolicy"
                    value={formData.returnPolicy}
                    onChange={(e) => handleChange('returnPolicy', e.target.value)}
                    rows={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prohibitedItems">Prohibited Items</Label>
                  <Textarea
                    id="prohibitedItems"
                    value={formData.prohibitedItems}
                    onChange={(e) => handleChange('prohibitedItems', e.target.value)}
                    rows={4}
                    placeholder="Enter prohibited items list..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Analytics & Reporting Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reportSchedule">Report Schedule</Label>
                  <Select
                    value={formData.reportSchedule}
                    onValueChange={(value) => handleChange('reportSchedule', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customMetrics">Custom Metrics</Label>
                  <Textarea
                    id="customMetrics"
                    value={formData.customMetrics}
                    onChange={(e) => handleChange('customMetrics', e.target.value)}
                    rows={4}
                    placeholder="Enter custom metrics configuration..."
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Analytics Enabled</Label>
                    <div className="text-sm text-neutral-500">
                      Enable analytics tracking
                    </div>
                  </div>
                  <Switch
                    checked={formData.analyticsEnabled}
                    onCheckedChange={(value) => handleChange('analyticsEnabled', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access">
            <Card>
              <CardHeader>
                <CardTitle>Access Control Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Role Permissions</Label>
                  <div className="space-y-4">
                    {Object.entries(formData.rolePermissions).map(([role, permissions]) => (
                      <div key={role} className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="capitalize">{role}</Label>
                          <div className="text-sm text-neutral-500">
                            {permissions.join(', ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>Approval Workflow</Label>
                    <div className="text-sm text-neutral-500">
                      Require approval for sensitive operations
                    </div>
                  </div>
                  <Switch
                    checked={formData.approvalWorkflow}
                    onCheckedChange={(value) => handleChange('approvalWorkflow', value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <div className="flex justify-end pt-6">
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Tabs>
    </div>
  );
} 
